import React, { forwardRef, useContext, useImperativeHandle, useState } from "react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { getAPIAxios } from "../../../../../helper/constants/uri";
import ShLoadingIndicatorModalLazy from "../../../../../components/loading-indicator/shLoadingIndicatorModalLazy";
import ShUserMessage from "../../../../../components/user-message/user-message";

import styles from "./shCheckoutStripe.module.scss";
import { checkStripeUpdate } from "../../helper/stripe/shStripeInfoChecker";
import { trackPosthog } from "../../../../../helper/tracking/tracking";
import { AuthContext } from "../../../../../provider/authProvider";

const ShCheckkoutStripeCreditcard = (
    {
        url = null,
        postData = {},
        paymentSucceeded = (params: { transactionId: string, transactionDate: string} ) => { }
    },
    ref
) => {
    const authContext = useContext(AuthContext);

    const [ errorMessage, setErrorMessage ] = useState(null);
    const [ processing, setProcessing ] = useState(false);

    const stripe = useStripe();
    const elements = useElements();

    const cardStyle = {
        style: {
            base: {
                iconColor:"#2a3349",
                color: "#2a3349",
                fontFamily: 'DM Sans, sans-serif',
                fontSmoothing: "antialiased",
                fontSize: "16px",
                '::placeholder': {
                    color: '#9398a3'
                },
                ':-webkit-autofill': {
                    color: '#2a3349',
                },

            },
            invalid: {
                color: "#f30000",
                iconColor: "#f30000"
            }
        }
    };

    const handleChange = async (event) => {
        // Listen for changes in the CardElement and display any errors as the customer types their card details
        setErrorMessage(event.error ? event.error.message : "");
    };

    const handleSubmit = async () => {
        // already working on it
        if (processing) { return; }

        const axios = getAPIAxios();

        try {
            setProcessing(true);

            // perform standard transaction
            const res = await axios.post(url, postData);
            const clientSecret = res.data.clientSecret;
            const setupIntentClientSecret = res.data.setupIntentClientSecret;

            let payload;

            if (clientSecret) {
                payload = await stripe.confirmCardPayment(clientSecret, {
                    payment_method: {
                        card: elements.getElement(CardElement)
                    }
                });
            }

            if (setupIntentClientSecret) {
                payload = await stripe.confirmCardSetup(setupIntentClientSecret, {
                    payment_method: {
                        card: elements.getElement(CardElement)
                    }
                });
            }

            if (payload.error) {
                trackPosthog("payload error stripe", payload.error);
                setErrorMessage(`${payload.error.message}`);

                // anyway check stripe update
                const stripeInfo = await checkStripeUpdate(2);

                if (stripeInfo) {
                    await paymentSucceeded(stripeInfo);
                }

            } else {
                setErrorMessage(null);

                const stripeInfo = await checkStripeUpdate();

                if (!stripeInfo) {
                    trackPosthog("no stripe info", { url, postData } );
                    setErrorMessage("Eventuell dauert es noch einen Moment bis die Zahlung bei uns angekommen ist.");
                    await paymentSucceeded(null);
                } else {
                    await paymentSucceeded(stripeInfo);
                }

            }

        } catch(err) {
            console.log(err);
            setErrorMessage("Unbekannter Fehler passiert");

            await authContext.updateOwnOverview();
        } finally {
            setProcessing(false);
        }
    };

    useImperativeHandle(ref, () => ({
        processPayment: () => handleSubmit()
    }));

    return <>
        <ShLoadingIndicatorModalLazy isOpen={processing} />

        <form id="payment-form" onSubmit={handleSubmit}>
            <div className={styles.wrapper}>
                <CardElement id="card-element" options={cardStyle} onChange={handleChange} />
            </div>

            <ShUserMessage error={true} message={errorMessage} className="mt-2 ml-3"/>
        </form>
    </>;

};

export default forwardRef(ShCheckkoutStripeCreditcard);
